import type { UserRole } from "@myapp/contract";
import { PUSH_TARGETS, type PushTargetOption } from "../model/pushTargets";

/**
 * Naming a target in prose, in both directions.
 *
 * `describeTargetOption` reads the catalog entry the compose form currently holds;
 * `describePushTarget` works backwards from a URL, which is all the Verlauf has. Both end up in
 * the same sentence, so a message reads the same after it was sent as it did before.
 */

/**
 * Roles come out of `routeConfig` in the singular ("Büroangestellter") via
 * `getPositionFromRole`; a sentence about who a page is for wants the plural, so the words are
 * repeated here in that form rather than bent at the call site.
 */
const ROLE_PLURAL: Record<string, string> = {
  WORKER: "Handwerker",
  MANAGER: "Büroangestellte",
  DIRECTOR: "Leiter",
  SUPERUSER: "Superadmins",
};

/**
 * Who can open the target, as a list.
 *
 * SUPERUSER sits on nearly every role-guarded route as a way in for support, so naming it
 * alongside the role the page is actually for would be noise. It is only mentioned when it is
 * the sole audience — the settings area.
 */
export function describeAudience(roles: UserRole[] | undefined): string | null {
  if (!roles || roles.length === 0) return null;

  const named = roles.length > 1 ? roles.filter((role) => role !== "SUPERUSER") : roles;
  const words = (named.length > 0 ? named : roles)
    .map((role) => (role ? ROLE_PLURAL[role] : undefined))
    .filter(Boolean);

  return words.length > 0 ? words.join(", ") : null;
}

/** The catalog label without its "…", which only means "there is more to fill in below". */
const plainLabel = (target: PushTargetOption) => target.label.replace(/\s*…\s*$/, "");

export function describeTargetOption(
  target: PushTargetOption,
  values: { param1: string; param2: string },
): string {
  if (!target.describe) return plainLabel(target);

  const trimmed = { param1: values.param1.trim(), param2: values.param2.trim() };
  /**
   * Before the ids are filled in there is nothing to name, and "Reparaturauftrag " with a
   * trailing space is worse than the plain label.
   *
   * Only the token-bearing params count. A lookup-only param — the Auftragsnummer in front of
   * the Handwerker-Termin — is absent by definition when this is called from a URL, and
   * treating that as "incomplete" is what made the Verlauf print the bare label instead of
   * naming the Termin.
   */
  const needed = (target.params ?? []).filter((param) => param.token);
  const missing = needed.some((param) => trimmed[param.field] === "");
  return missing ? plainLabel(target) : target.describe(trimmed);
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * A URL back into its catalog entry.
 *
 * The path is turned into a pattern by escaping it and then swapping the `:token` literals —
 * which contain no regex metacharacters and so survive escaping unchanged — for capture groups.
 * Groups come out in the order the tokens appear in the path, which is not necessarily the
 * order the params are declared in, hence the sort.
 */
function matchTarget(url: string) {
  for (const target of PUSH_TARGETS) {
    const tokenParams = (target.params ?? [])
      .filter((param) => param.token)
      .sort((a, b) => target.path.indexOf(a.token!) - target.path.indexOf(b.token!));

    let pattern = escapeRegExp(target.path);
    tokenParams.forEach((param) => {
      pattern = pattern.replace(param.token!, "([^/?&]+)");
    });

    const match = new RegExp(`^${pattern}$`).exec(url);
    if (!match) continue;

    const values = { param1: "", param2: "" };
    tokenParams.forEach((param, index) => {
      values[param.field] = decodeURIComponent(match[index + 1] ?? "");
    });

    return { target, values };
  }

  return null;
}

/**
 * What a sent message's `url` opened, for the Verlauf. Falls back to the raw path — a message
 * sent before this field became a picker, or through the "Eigener Pfad" escape hatch, still has
 * to show something.
 */
export function describePushTarget(url: string): string {
  const match = matchTarget(url);
  return match ? describeTargetOption(match.target, match.values) : url;
}
